import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import NavigationBar from '@/components/NavigationBar';
import { useState, useMemo } from 'react';
import { 
  Calendar, 
  Users, 
  Vote, 
  BarChart3, 
  Share2, 
  Bell, 
  FileText, 
  Clock, 
  Shield,
  Smartphone,
  Globe,
  Crown,
  Search,
  Settings,
  Upload,
  QrCode,
  Image,
  Repeat,
  UserCheck,
  Building,
  Heart,
  Download,
  Tag,
  PlayCircle,
  CheckCircle,
  AlertCircle,
  HelpCircle
} from 'lucide-react';

const Help = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  const allFeatures = [
    {
      icon: Calendar,
      title: "Event Creation",
      description: "Create and customize events with multiple date options",
      category: "basic",
      testSteps: [
        "Go to Dashboard → Create Event",
        "Add event name, description, and location",
        "Add multiple date/time options",
        "Set voting deadline and preferences",
        "Click 'Create Event' to save"
      ],
      details: [
        "Add multiple date/time options for your event",
        "Set event descriptions and details",
        "Customize voting deadlines",
        "Enable or disable guest comments",
        "Create recurring events",
        "Use professional templates"
      ]
    },
    {
      icon: Users,
      title: "Guest Management",
      description: "Invite and manage your event participants",
      category: "basic",
      testSteps: [
        "Open your event from the dashboard",
        "Click 'Guest List' tab",
        "Add guests individually or bulk import",
        "Send invitations via email",
        "Track responses in real-time"
      ],
      details: [
        "Add guests via email addresses",
        "Bulk import guest lists from CSV",
        "Track guest responses and engagement",
        "Send automated reminders",
        "Manage guest contacts database",
        "Family group management"
      ]
    },
    {
      icon: Vote,
      title: "Smart Voting & RSVP",
      description: "Advanced voting system with real-time updates",
      category: "basic",
      testSteps: [
        "Share voting link with guests",
        "Guests vote on available dates",
        "View real-time results in analytics",
        "Use conflict matrix for optimization",
        "Finalize the best date option"
      ],
      details: [
        "Multiple voting options per guest",
        "Real-time vote tracking",
        "Weighted voting for premium users",
        "Automatic conflict resolution",
        "Custom RSVP forms",
        "Conditional logic for forms"
      ]
    },
    {
      icon: BarChart3,
      title: "Analytics & Insights",
      description: "Detailed analytics for your events and hosting patterns",
      category: "analytics",
      testSteps: [
        "Go to Analytics from main navigation",
        "View event performance metrics",
        "Check guest engagement stats",
        "Export data for external analysis",
        "Use Host Intelligence for insights"
      ],
      details: [
        "View voting patterns and trends",
        "Track guest engagement metrics",
        "Export data for external analysis",
        "Response time analytics",
        "Host Intelligence dashboard",
        "Advanced analytics for premium users"
      ]
    },
    {
      icon: Image,
      title: "AI Image Generation",
      description: "Generate custom event images and invitations",
      category: "advanced",
      testSteps: [
        "Open event details page",
        "Click 'Generate Image' button",
        "Enter image description prompt",
        "Select image style and format",
        "Download or share generated image"
      ],
      details: [
        "AI-powered invitation images",
        "Custom event thumbnails",
        "QR code integration in images",
        "Multiple image styles available",
        "Automatic text overlay options"
      ]
    },
    {
      icon: QrCode,
      title: "QR Code & Sharing",
      description: "Multiple ways to share your events",
      category: "basic",
      testSteps: [
        "Open event from dashboard",
        "Click 'Share' or QR code icon",
        "Download QR code image",
        "Share link via email or social media",
        "Test QR code with phone camera"
      ],
      details: [
        "Shareable voting links",
        "QR codes for quick access",
        "Email invitations",
        "Social media integration",
        "Custom QR code styling",
        "Bulk sharing options"
      ]
    },
    {
      icon: Building,
      title: "Client Management",
      description: "Professional client and team management",
      category: "professional",
      testSteps: [
        "Go to Admin Panel → Client Management",
        "Add new client with details",
        "Create client portal access",
        "Assign events to clients",
        "Generate client-specific reports"
      ],
      details: [
        "Client database management",
        "Client portal access",
        "Team collaboration features",
        "Client-specific branding",
        "Professional event templates",
        "Client analytics and reporting"
      ]
    },
    {
      icon: Heart,
      title: "Family Management",
      description: "Organize family events and groups",
      category: "family",
      testSteps: [
        "Go to Family Dashboard",
        "Create family group",
        "Invite family members",
        "Create family-specific events",
        "Use family event templates"
      ],
      details: [
        "Family group creation",
        "Member invitation system",
        "Family-specific event templates",
        "Shared family calendar",
        "Family member permissions",
        "Recurring family events"
      ]
    },
    {
      icon: Repeat,
      title: "Recurring Events",
      description: "Set up repeating events with automation",
      category: "advanced",
      testSteps: [
        "Create event normally",
        "Enable 'Recurring Event' option",
        "Set recurrence pattern (daily/weekly/monthly)",
        "Configure end conditions",
        "Monitor generated instances"
      ],
      details: [
        "Daily, weekly, monthly, yearly patterns",
        "Automatic instance generation",
        "End date or occurrence limits",
        "Individual instance modifications",
        "Bulk management of series"
      ]
    },
    {
      icon: Tag,
      title: "Promo Codes & Subscriptions",
      description: "Promotional codes and subscription management",
      category: "admin",
      testSteps: [
        "Go to Profile → Subscription",
        "Enter promo code if available",
        "View available subscription plans",
        "Upgrade to premium features",
        "Manage billing and payments"
      ],
      details: [
        "Promo code redemption",
        "Subscription plan management",
        "Premium feature access",
        "Billing and payment handling",
        "Usage limit tracking"
      ]
    },
    {
      icon: Settings,
      title: "System Administration",
      description: "Administrative features and system management",
      category: "admin",
      testSteps: [
        "Access Admin Panel (admin users only)",
        "Manage users and permissions",
        "Configure system settings",
        "Monitor security logs",
        "Manage promo codes and features"
      ],
      details: [
        "User role management",
        "Security audit logs",
        "System configuration",
        "Feature flag management",
        "Performance monitoring",
        "Bulk operations"
      ]
    },
    {
      icon: Bell,
      title: "Notifications & Reminders",
      description: "Automated notification system",
      category: "basic",
      testSteps: [
        "Configure notification preferences",
        "Set up automatic reminders",
        "Test email delivery",
        "Monitor notification logs",
        "Customize message templates"
      ],
      details: [
        "Email reminders for guests",
        "Vote deadline notifications",
        "Event result announcements",
        "Real-time voting updates",
        "Custom notification templates",
        "Delivery tracking and logs"
      ]
    },
    {
      icon: Download,
      title: "Export & Import",
      description: "Data export and import capabilities",
      category: "advanced",
      testSteps: [
        "Go to Analytics → Export",
        "Select data range and format",
        "Download CSV/PDF reports",
        "Import guest lists from CSV",
        "Backup event data"
      ],
      details: [
        "CSV data export",
        "PDF report generation",
        "Guest list import/export",
        "Event data backup",
        "Analytics data download",
        "Bulk operations support"
      ]
    }
  ];

  const quickStartGuides = {
    basic: [
      {
        step: 1,
        title: "Create Your Event",
        description: "Go to Dashboard → Create Event, add name, dates, and details",
        testAction: "✓ Test: Create a sample event with 3 date options"
      },
      {
        step: 2,
        title: "Add Guests",
        description: "Click Guest List tab, add email addresses individually or bulk import",
        testAction: "✓ Test: Add yourself as a guest to test the flow"
      },
      {
        step: 3,
        title: "Share & Vote",
        description: "Copy the voting link or scan QR code to test voting experience",
        testAction: "✓ Test: Vote on your own event using the guest link"
      },
      {
        step: 4,
        title: "Monitor Results",
        description: "View real-time results in Analytics and conflict matrix",
        testAction: "✓ Test: Check analytics after voting"
      },
      {
        step: 5,
        title: "Finalize Date",
        description: "Choose optimal date and notify guests of final decision",
        testAction: "✓ Test: Mark event as finalized"
      }
    ],
    professional: [
      {
        step: 1,
        title: "Set Up Client",
        description: "Go to Admin Panel → Client Management, add new client",
        testAction: "✓ Test: Create a sample client profile"
      },
      {
        step: 2,
        title: "Create Professional Event",
        description: "Use professional templates and assign to client",
        testAction: "✓ Test: Create event using a professional template"
      },
      {
        step: 3,
        title: "Configure Branding",
        description: "Customize branding and client portal settings",
        testAction: "✓ Test: Apply custom branding to event"
      },
      {
        step: 4,
        title: "Generate Reports",
        description: "Create client-specific analytics and reports",
        testAction: "✓ Test: Export client analytics report"
      }
    ],
    family: [
      {
        step: 1,
        title: "Create Family Group",
        description: "Go to Family Dashboard, create new family group",
        testAction: "✓ Test: Set up a family group with sample members"
      },
      {
        step: 2,
        title: "Invite Members",
        description: "Send family invitations via email",
        testAction: "✓ Test: Invite yourself to the family group"
      },
      {
        step: 3,
        title: "Plan Family Event",
        description: "Use family templates for recurring events",
        testAction: "✓ Test: Create a recurring family dinner event"
      }
    ]
  };

  const troubleshooting = [
    {
      icon: AlertCircle,
      issue: "Guests not receiving emails",
      solution: "Check spam folders, verify email addresses, check notification settings",
      testSteps: ["Test with your own email", "Check admin notification logs", "Verify SMTP settings"]
    },
    {
      icon: HelpCircle,
      issue: "QR code not working",
      solution: "Ensure event is active, check QR code image quality, test with different devices",
      testSteps: ["Scan QR with phone camera", "Check event status", "Regenerate QR if needed"]
    },
    {
      icon: Users,
      issue: "Guest can't vote",
      solution: "Verify voting deadline hasn't passed, check guest permissions, ensure event is active",
      testSteps: ["Check event deadline", "Verify guest list", "Test voting link directly"]
    },
    {
      icon: BarChart3,
      issue: "Analytics not updating",
      solution: "Refresh page, check data sync, verify permissions",
      testSteps: ["Force refresh browser", "Check real-time updates", "Verify user permissions"]
    }
  ];

  const premiumFeatures = [
    {
      icon: Crown,
      title: "Advanced Analytics",
      description: "Detailed insights, host intelligence, and advanced reporting capabilities"
    },
    {
      icon: Users,
      title: "Unlimited Guests & Events",
      description: "No limits on events, guests, or features"
    },
    {
      icon: Building,
      title: "Professional Features",
      description: "Client management, custom branding, team collaboration"
    },
    {
      icon: Image,
      title: "AI Image Generation",
      description: "Unlimited AI-generated event images and invitations"
    },
    {
      icon: Calendar,
      title: "Priority Support",
      description: "Get help faster with premium support and dedicated assistance"
    },
    {
      icon: Shield,
      title: "Enhanced Security & Privacy",
      description: "Advanced security features, audit logs, and data protection"
    }
  ];

  // Filter features based on search query
  const filteredFeatures = useMemo(() => {
    if (!searchQuery) return allFeatures;
    
    return allFeatures.filter(feature =>
      feature.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      feature.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      feature.details.some(detail => detail.toLowerCase().includes(searchQuery.toLowerCase())) ||
      feature.category.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [searchQuery, allFeatures]);

  const filteredTroubleshooting = useMemo(() => {
    if (!searchQuery) return troubleshooting;
    
    return troubleshooting.filter(item =>
      item.issue.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.solution.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [searchQuery, troubleshooting]);

  return (
    <div className="min-h-screen bg-background">
      <NavigationBar />
      
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-4">Help & Documentation</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-6">
            Comprehensive guide to EventDatePicker's features with step-by-step testing instructions.
          </p>
          
          {/* Search */}
          <div className="max-w-md mx-auto relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search features, guides, or troubleshooting..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Navigation Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="features">Features</TabsTrigger>
            <TabsTrigger value="guides">Quick Start</TabsTrigger>
            <TabsTrigger value="troubleshooting">Troubleshooting</TabsTrigger>
            <TabsTrigger value="premium">Premium</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-8">
            {/* Feature Categories Overview */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="text-center cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveTab('features')}>
                <CardHeader>
                  <Calendar className="h-12 w-12 text-primary mx-auto mb-2" />
                  <CardTitle>Basic Features</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">Event creation, guest management, voting</p>
                </CardContent>
              </Card>
              
              <Card className="text-center cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveTab('features')}>
                <CardHeader>
                  <Building className="h-12 w-12 text-primary mx-auto mb-2" />
                  <CardTitle>Professional</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">Client management, branding, teams</p>
                </CardContent>
              </Card>
              
              <Card className="text-center cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveTab('features')}>
                <CardHeader>
                  <BarChart3 className="h-12 w-12 text-primary mx-auto mb-2" />
                  <CardTitle>Analytics</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">Advanced insights, reporting, intelligence</p>
                </CardContent>
              </Card>
              
              <Card className="text-center cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveTab('premium')}>
                <CardHeader>
                  <Crown className="h-12 w-12 text-primary mx-auto mb-2" />
                  <CardTitle>Premium</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">Advanced features, unlimited access</p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="features" className="space-y-8">

            {/* All Features */}
            <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-6">
              {filteredFeatures.map((feature) => (
                <Card key={feature.title} className="h-full">
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-2">
                      <feature.icon className="h-6 w-6 text-primary" />
                      <div className="flex-1">
                        <CardTitle className="text-xl">{feature.title}</CardTitle>
                        <Badge variant="outline" className="mt-1 text-xs">
                          {feature.category}
                        </Badge>
                      </div>
                    </div>
                    <CardDescription>{feature.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* Test Steps */}
                      {feature.testSteps && (
                        <div>
                          <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                            <PlayCircle className="h-4 w-4 text-primary" />
                            How to Test:
                          </h4>
                          <ol className="space-y-1 text-sm text-muted-foreground">
                            {feature.testSteps.map((step, index) => (
                              <li key={index} className="flex items-start gap-2">
                                <span className="text-primary font-semibold min-w-[1.5rem]">{index + 1}.</span>
                                {step}
                              </li>
                            ))}
                          </ol>
                        </div>
                      )}
                      
                      {/* Feature Details */}
                      <div>
                        <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-primary" />
                          Features:
                        </h4>
                        <ul className="space-y-1">
                          {feature.details.map((detail, index) => (
                            <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                              <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                              {detail}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="guides" className="space-y-8">
            {/* Quick Start Guides */}
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-semibold mb-6 flex items-center gap-3">
                  <Calendar className="h-6 w-6 text-primary" />
                  Basic Event Planning
                </h3>
                <div className="grid md:grid-cols-5 gap-4">
                  {quickStartGuides.basic.map((item) => (
                    <Card key={item.step} className="text-center">
                      <CardHeader>
                        <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                          {item.step}
                        </div>
                        <CardTitle className="text-lg">{item.title}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground mb-3">
                          {item.description}
                        </p>
                        <Badge variant="secondary" className="text-xs">
                          {item.testAction}
                        </Badge>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
              
              <div>
                <h3 className="text-2xl font-semibold mb-6 flex items-center gap-3">
                  <Building className="h-6 w-6 text-primary" />
                  Professional Setup
                </h3>
                <div className="grid md:grid-cols-4 gap-4">
                  {quickStartGuides.professional.map((item) => (
                    <Card key={item.step} className="text-center">
                      <CardHeader>
                        <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                          {item.step}
                        </div>
                        <CardTitle className="text-lg">{item.title}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground mb-3">
                          {item.description}
                        </p>
                        <Badge variant="secondary" className="text-xs">
                          {item.testAction}
                        </Badge>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
              
              <div>
                <h3 className="text-2xl font-semibold mb-6 flex items-center gap-3">
                  <Heart className="h-6 w-6 text-primary" />
                  Family Events
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  {quickStartGuides.family.map((item) => (
                    <Card key={item.step} className="text-center">
                      <CardHeader>
                        <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                          {item.step}
                        </div>
                        <CardTitle className="text-lg">{item.title}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground mb-3">
                          {item.description}
                        </p>
                        <Badge variant="secondary" className="text-xs">
                          {item.testAction}
                        </Badge>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="troubleshooting" className="space-y-8">
            {/* Troubleshooting Guide */}
            <div>
              <h3 className="text-2xl font-semibold mb-6 flex items-center gap-3">
                <HelpCircle className="h-6 w-6 text-primary" />
                Common Issues & Solutions
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                {filteredTroubleshooting.map((item, index) => (
                  <Card key={index}>
                    <CardHeader>
                      <div className="flex items-center gap-3 mb-2">
                        <item.icon className="h-6 w-6 text-destructive" />
                        <CardTitle className="text-lg text-destructive">{item.issue}</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-semibold text-sm mb-2">Solution:</h4>
                          <p className="text-sm text-muted-foreground">{item.solution}</p>
                        </div>
                        <div>
                          <h4 className="font-semibold text-sm mb-2">Test Steps:</h4>
                          <ol className="space-y-1 text-sm text-muted-foreground">
                            {item.testSteps.map((step, stepIndex) => (
                              <li key={stepIndex} className="flex items-start gap-2">
                                <span className="text-primary font-semibold min-w-[1.5rem]">{stepIndex + 1}.</span>
                                {step}
                              </li>
                            ))}
                          </ol>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="premium" className="space-y-8">

            {/* Premium Features */}
            <div>
              <h3 className="text-2xl font-semibold mb-6 flex items-center gap-3">
                <Crown className="h-6 w-6 text-primary" />
                Premium Features
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {premiumFeatures.map((feature) => (
                  <Card key={feature.title} className="text-center h-full">
                    <CardHeader>
                      <feature.icon className="h-12 w-12 text-primary mx-auto mb-4" />
                      <CardTitle className="text-lg">{feature.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">
                        {feature.description}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Tips & Best Practices */}
            <div>
              <h3 className="text-2xl font-semibold mb-6 flex items-center gap-3">
                <Globe className="h-6 w-6 text-primary" />
                Tips & Best Practices
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Smartphone className="h-5 w-5" />
                      Mobile Optimization
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      Our platform is fully optimized for mobile devices. Guests can easily vote on their phones.
                    </p>
                    <ul className="space-y-1 text-sm">
                      <li>• QR codes work great for mobile sharing</li>
                      <li>• Touch-friendly voting interface</li>
                      <li>• Responsive design across all devices</li>
                    </ul>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="h-5 w-5" />
                      Timing Your Events
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      Set appropriate voting deadlines to ensure maximum participation.
                    </p>
                    <ul className="space-y-1 text-sm">
                      <li>• Give guests at least 2-3 days to respond</li>
                      <li>• Send reminders 24 hours before deadline</li>
                      <li>• Consider time zones for remote participants</li>
                    </ul>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="h-5 w-5" />
                      Security & Privacy
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      Keep your events secure with our built-in privacy features.
                    </p>
                    <ul className="space-y-1 text-sm">
                      <li>• Use shareable links instead of email lists</li>
                      <li>• Set voting deadlines to prevent late responses</li>
                      <li>• Review guest lists before sending invites</li>
                    </ul>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5" />
                      Analytics Best Practices
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      Make data-driven decisions with our analytics features.
                    </p>
                    <ul className="space-y-1 text-sm">
                      <li>• Check conflict matrix for optimal dates</li>
                      <li>• Monitor response rates in real-time</li>
                      <li>• Export data for external analysis</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Getting Premium */}
            <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
              <CardHeader className="text-center">
                <Crown className="h-16 w-16 text-primary mx-auto mb-4" />
                <CardTitle className="text-2xl">Upgrade to Premium</CardTitle>
                <CardDescription>
                  Unlock all features and get unlimited access to EventDatePicker
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center space-y-4">
                <div className="space-y-2">
                  <p className="text-sm">
                    <strong>Have a promo code?</strong> Go to Profile → Subscription to redeem it.
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Contact sales for enterprise features and custom solutions.
                  </p>
                </div>
                <Button size="lg" className="w-full max-w-sm">
                  View Subscription Plans
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Support Section */}
        {!searchQuery && (
          <Card className="bg-muted/50 mt-16">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">Need More Help?</CardTitle>
              <CardDescription>
                We're here to help you make the most of EventDatePicker
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <div className="flex flex-wrap gap-4 justify-center">
                <Badge variant="secondary" className="text-sm py-2 px-4">
                  📧 support@eventdatepicker.com
                </Badge>
                <Badge variant="secondary" className="text-sm py-2 px-4">
                  💬 Live Chat
                </Badge>
                <Badge variant="secondary" className="text-sm py-2 px-4">
                  📖 Knowledge Base
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                Premium users get priority support with faster response times.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Help;